/**
 * Organisation usage-stat derivation for the org detail page (Trello #372).
 *
 * Every Broker Doc Assist question registers a job row, so its completion bumps the
 * same org-level `completedCount` an ROA or a renewal does — which made a chatty org
 * read as having run 22 platform jobs when 16 of those were chat questions. The
 * backend now splits the figure (`completedJobsCount` + `docAssistCount`) from the
 * per-service breakdown it already stores; this module is the single place the UI
 * reads that split, so the stats card and the history chart can't disagree.
 *
 * Deploy-lag safe: the admin ships from its own pipeline and can reach an
 * environment before the backend that supplies the new fields does. When they're
 * absent we fall back to the raw total and the caller HIDES the Doc Assist figure
 * rather than rendering a misleading 0 (general/fe-degrade-when-backend-field-absent).
 */

export interface OrgUsageRow {
  period: string;
  /** Credits used this period. Fractional — Doc Assist bills 0.1 per question. */
  submittedCount: number;
  /** TOTAL completed runs, Doc Assist questions included. */
  completedCount: number;
  /** Completed runs excluding Doc Assist. Absent on a backend older than #372. */
  completedJobsCount?: number;
  /** Completed Doc Assist questions. Absent on a backend older than #372. */
  docAssistCount?: number;
  failedCount: number;
  totalCostUsd?: number;
}

/**
 * The colour the Doc Assist figure and its chart bar share, so the stat tile and
 * the bar can't drift apart visually (they're the same quantity).
 */
export const DOC_ASSIST_COLOR = '#805ad5';

/**
 * Coerce a possibly-absent stored counter to a finite number.
 *
 * File-private on purpose: `src/utils/` carries no shared numeric-coercion helper
 * (checked — `format.ts` exports string FORMATTERS only, and `period.ts`'s
 * `Number.isFinite` use is inline date validation), so there is nothing here to
 * reuse. If a shared one is ever added, this should fold into it.
 */
function toFiniteNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Does the API response actually carry the #372 split?
 *
 * Gates the "Doc Assist Questions" figure on DATA presence, not just on the page
 * rendering — an admin deployed ahead of the backend would otherwise show a
 * permanent "0" that reads as "nobody uses Doc Assist" rather than "not reported
 * yet". A row where the field is PRESENT and genuinely 0 is real data and shows.
 *
 * Requires BOTH halves of the split on the same row, deliberately. The backend
 * emits them together from one destructure, so a row carrying only one is not a
 * shape we expect — but if it ever happened, gating on `docAssistCount` alone
 * would surface "Doc Assist Questions: 16" NEXT TO a "Completed Jobs" that had
 * silently fallen back to the un-split total, i.e. the exact double-count this
 * change exists to remove. Demanding the pair makes that state unreachable:
 * either both figures are the split ones, or neither is.
 */
export function hasDocAssistData(rows: OrgUsageRow[]): boolean {
  return rows.some((row) => row.docAssistCount !== undefined && row.completedJobsCount !== undefined);
}

/**
 * Completed runs to render as "Completed Jobs" — Doc Assist excluded when the
 * backend reports the split, else the raw total (pre-#372 behaviour).
 */
export function resolveCompletedJobs(row: OrgUsageRow): number {
  return row.completedJobsCount === undefined
    ? toFiniteNumber(row.completedCount)
    : toFiniteNumber(row.completedJobsCount);
}

/** Completed Doc Assist questions to render as its own figure (0 when unreported). */
export function resolveDocAssistCount(row: OrgUsageRow): number {
  return toFiniteNumber(row.docAssistCount);
}

/** One bar in the usage-history chart. */
export interface UsageChartRow {
  period: string;
  /** Doc-Assist-excluded, so the chart matches the "Completed Jobs" stat above it. */
  completedJobs: number;
  docAssistQuestions: number;
  totalCostUsd: number;
}

/**
 * Map the raw history (newest-first, as the API returns it) into oldest-first chart
 * rows with the split already applied — so the bars and the current-period card read
 * from ONE derivation.
 */
export function toUsageChartRows(rows: OrgUsageRow[]): UsageChartRow[] {
  return [...rows].reverse().map((row) => ({
    period: row.period,
    completedJobs: resolveCompletedJobs(row),
    docAssistQuestions: resolveDocAssistCount(row),
    totalCostUsd: toFiniteNumber(row.totalCostUsd),
  }));
}
